<template>
  <div class="relative min-h-screen flex items-center justify-center overflow-hidden p-6">
    <!-- Animated background -->
    <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-primary-dark to-gray-900">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
      <div class="absolute inset-0 dot-grid opacity-[0.03]"></div>
    </div>

    <div class="relative z-10 w-full max-w-[500px]">
      <!-- Glass card -->
      <div class="glass-card rounded-2xl p-8 sm:p-10">
        <div class="flex justify-center mb-8">
          <img src="@/assets/images/sellhub-logo.png" alt="SellHub" class="h-10 w-auto brightness-0 invert opacity-90" />
        </div>

        <div class="text-center mb-8">
          <h1 class="text-2xl font-bold text-white mb-2">Créer un compte</h1>
          <p class="text-gray-400 text-sm">Commencez à gérer votre activité avec SellHub</p>
        </div>

        <!-- Error -->
        <div v-if="error" class="flex items-start gap-3 bg-red-500/10 border border-red-500/20 text-red-300 px-4 py-3 rounded-xl mb-6 text-sm">
          <i class="fas fa-exclamation-circle mt-0.5 text-red-400"></i>
          <span>{{ error }}</span>
        </div>

        <!-- Step indicator -->
        <div class="mb-6">
          <div class="flex items-center gap-3 mb-2">
            <div class="flex-1 h-1.5 rounded-full overflow-hidden bg-white/5">
              <div class="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-500"
                :style="{ width: step === 1 ? '50%' : '100%' }"></div>
            </div>
          </div>
          <p class="text-xs text-gray-500 text-right">Étape {{ step }} sur 2</p>
        </div>

        <!-- Step 1: Personal info -->
        <form v-if="step === 1" @submit.prevent="goToStep2" class="space-y-4">
          <div>
            <label class="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Nom complet</label>
            <div class="relative">
              <i class="fas fa-user absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm"></i>
              <input v-model="form.name" type="text" placeholder="Votre nom complet" required
                :class="[
                  'w-full pl-11 pr-4 py-3 bg-white/5 border rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all',
                  validationErrors.name ? 'border-red-500/50' : 'border-white/10'
                ]" />
            </div>
            <p v-if="validationErrors.name" class="text-red-400 text-xs mt-1.5">{{ validationErrors.name[0] }}</p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Email</label>
            <div class="relative">
              <i class="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm"></i>
              <input v-model="form.email" type="email" placeholder="votre@email.com" required
                :class="[
                  'w-full pl-11 pr-4 py-3 bg-white/5 border rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all',
                  validationErrors.email ? 'border-red-500/50' : 'border-white/10'
                ]" />
            </div>
            <p v-if="validationErrors.email" class="text-red-400 text-xs mt-1.5">{{ validationErrors.email[0] }}</p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Nom de l'entreprise</label>
            <div class="relative">
              <i class="fas fa-building absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm"></i>
              <input v-model="form.company_name" type="text" placeholder="Nom de votre entreprise" required
                :class="[
                  'w-full pl-11 pr-4 py-3 bg-white/5 border rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all',
                  validationErrors.company_name ? 'border-red-500/50' : 'border-white/10'
                ]" />
            </div>
            <p v-if="validationErrors.company_name" class="text-red-400 text-xs mt-1.5">{{ validationErrors.company_name[0] }}</p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Type d'activité</label>
            <div class="relative">
              <i class="fas fa-tag absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm"></i>
              <select v-model="form.business_type"
                :class="[
                  'w-full pl-11 pr-4 py-3 bg-white/5 border rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all appearance-none',
                  validationErrors.business_type ? 'border-red-500/50' : 'border-white/10'
                ]">
                <option value="" class="bg-gray-900 text-gray-400">Sélectionnez un type d'activité</option>
                <option value="retail" class="bg-gray-900 text-white">Commerce de détail</option>
                <option value="restaurant" class="bg-gray-900 text-white">Restaurant / Fast-food</option>
                <option value="service" class="bg-gray-900 text-white">Service</option>
                <option value="other" class="bg-gray-900 text-white">Autre</option>
              </select>
            </div>
            <p v-if="validationErrors.business_type" class="text-red-400 text-xs mt-1.5">{{ validationErrors.business_type[0] }}</p>
          </div>

          <button type="submit"
            class="w-full py-3.5 bg-gradient-to-r from-primary to-primary-dark hover:from-primary-light hover:to-primary text-white font-semibold text-sm rounded-xl transition-all duration-300 shadow-lg shadow-primary/20 hover:shadow-primary/30 flex items-center justify-center gap-2 mt-2">
            Suivant
            <i class="fas fa-arrow-right text-xs"></i>
          </button>
        </form>

        <!-- Step 2: Password -->
        <form v-if="step === 2" @submit.prevent="handleRegister" class="space-y-4">
          <div>
            <label class="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Mot de passe</label>
            <div class="relative">
              <i class="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm"></i>
              <input v-model="form.password" :type="passwordVisible ? 'text' : 'password'"
                placeholder="Choisissez un mot de passe sécurisé" required
                :class="[
                  'w-full pl-11 pr-12 py-3 bg-white/5 border rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all',
                  validationErrors.password ? 'border-red-500/50' : 'border-white/10'
                ]" />
              <button type="button" class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                @click="togglePasswordVisibility">
                <i :class="passwordVisible ? 'fas fa-eye-slash' : 'fas fa-eye'" class="text-sm"></i>
              </button>
            </div>
            <p v-if="validationErrors.password" class="text-red-400 text-xs mt-1.5">{{ validationErrors.password[0] }}</p>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Confirmation</label>
            <div class="relative">
              <i class="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm"></i>
              <input v-model="form.password_confirmation" :type="passwordVisible ? 'text' : 'password'"
                placeholder="Confirmez votre mot de passe" required
                class="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all" />
            </div>
          </div>

          <div class="flex gap-3 mt-2">
            <button type="button" @click="step = 1"
              class="flex-1 py-3.5 border border-white/10 text-gray-300 hover:bg-white/5 font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2">
              <i class="fas fa-arrow-left text-xs"></i>
              Retour
            </button>
            <button type="submit" :disabled="isLoading"
              class="flex-[2] py-3.5 bg-gradient-to-r from-primary to-primary-dark hover:from-primary-light hover:to-primary text-white font-semibold text-sm rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20 hover:shadow-primary/30 flex items-center justify-center gap-2">
              <i v-if="isLoading" class="fas fa-spinner fa-spin text-sm"></i>
              {{ isLoading ? 'Création...' : 'Créer mon compte' }}
            </button>
          </div>
        </form>

        <div class="text-center mt-6 pt-6 border-t border-white/5">
          <p class="text-sm text-gray-500">
            Vous avez déjà un compte ?
            <router-link :to="{ name: 'login' }" class="text-primary-light font-semibold hover:text-primary transition-colors">Se connecter</router-link>
          </p>
        </div>
      </div>

      <div class="text-center mt-6 space-y-1">
        <p class="text-gray-600 text-xs">© {{ new Date().getFullYear() }} SellHub. Tous droits réservés.</p>
        <p class="text-gray-600 text-xs">
          Powered by
          <a href="https://digita.sn" target="_blank" class="text-primary-light font-semibold hover:text-primary transition-colors">Digita SN</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore, useToastStore } from '@/store/modules'

export default defineComponent({
    name: 'RegisterView',

    setup() {
        const authStore = useAuthStore()
        const toastStore = useToastStore()
        const router = useRouter()
        const step = ref(1)
        const passwordVisible = ref(false)

        const form = reactive({
            name: '',
            email: '',
            company_name: '',
            business_type: '',
            password: '',
            password_confirmation: ''
        })

        const isLoading = ref(false)
        const error = ref(null)
        const validationErrors = ref({})

        const togglePasswordVisibility = () => {
            passwordVisible.value = !passwordVisible.value
        }

        const goToStep2 = () => {
            validationErrors.value = {}
            error.value = null

            if (!form.name) {
                validationErrors.value.name = ['Le nom est requis']
                return
            }

            if (!form.email) {
                validationErrors.value.email = ['L\'email est requis']
                return
            }

            if (!form.company_name) {
                validationErrors.value.company_name = ['Le nom de l\'entreprise est requis']
                return
            }

            if (!form.business_type) {
                validationErrors.value.business_type = ['Le type d\'activité est requis']
                return
            }

            step.value = 2
        }

        const handleRegister = async () => {
            isLoading.value = true
            error.value = null
            validationErrors.value = {}

            if (form.password !== form.password_confirmation) {
                validationErrors.value.password = ['Les mots de passe ne correspondent pas']
                isLoading.value = false
                return
            }

            try {
                await authStore.register(form)

                toastStore.addToast({
                    type: 'success',
                    message: 'Inscription réussie ! Veuillez vérifier votre email pour activer votre compte.',
                    duration: 6000
                })

                router.push({ name: 'login' })
            } catch (err) {
                console.error('Registration error:', err)

                if (err.response && err.response.status === 422) {
                    validationErrors.value = err.response.data.errors || {}
                    error.value = err.response.data.message || 'Erreur de validation'

                    if (validationErrors.value.name || validationErrors.value.email ||
                        validationErrors.value.company_name || validationErrors.value.business_type) {
                        step.value = 1
                    }
                }
                else {
                    error.value = err.response?.data?.message || "Une erreur est survenue lors de l'inscription"
                }
            } finally {
                isLoading.value = false
            }
        }

        return {
            form,
            isLoading,
            error,
            validationErrors,
            step,
            passwordVisible,
            togglePasswordVisibility,
            goToStep2,
            handleRegister
        }
    }
})
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
}

.dot-grid {
  background-image: radial-gradient(circle, rgba(255, 255, 255, 0.8) 1px, transparent 1px);
  background-size: 30px 30px;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(13, 148, 136, 0.5), transparent 70%);
  top: -10%;
  right: 20%;
  animation: float-1 20s ease-in-out infinite;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.4), transparent 70%);
  bottom: -5%;
  left: 10%;
  animation: float-2 25s ease-in-out infinite;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.3), transparent 70%);
  top: 40%;
  left: 40%;
  animation: float-3 18s ease-in-out infinite;
}

@keyframes float-1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(40px, -30px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

@keyframes float-2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-30px, 40px) scale(1.05); }
  66% { transform: translate(30px, -20px) scale(0.9); }
}

@keyframes float-3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(20px, 30px) scale(1.15); }
  66% { transform: translate(-40px, -10px) scale(0.85); }
}
</style>
